/**
 * InstallBanner — the mobile "add OmniWerk to your home screen" prompt.
 *
 * A bottom-anchored card that appears once on phones and tablets, offering
 * whichever install path the browser actually supports:
 *
 *   - **Android / Chromium** — a real one-tap install, driven by the
 *     `beforeinstallprompt` event parked in `@/pwa`.
 *   - **iOS Safari** — Apple ships no install API, so the CTA expands into the
 *     Share → Add to Home Screen steps instead.
 *   - **Anything else on mobile** (Firefox Android, a Chromium that declined
 *     to offer a prompt) — the same manual steps, worded for that platform's
 *     menu.
 *
 * Either way it also links out to the native desktop builds, which is the
 * better answer for anyone who lands here on a laptop-class device.
 *
 * It stays out of the way on purpose: hidden in the native shell and once
 * installed, held back a couple of seconds so it never lands during first
 * paint, `md:hidden` so it is phone-only, and snoozed for two weeks by a
 * dismissal (see `snoozeInstallBanner`).
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { isNativeHost } from "@/api/client";
import {
  NATIVE_DOWNLOADS_URL,
  getInstallState,
  installBannerSnoozed,
  mobilePlatform,
  promptInstall,
  snoozeInstallBanner,
  subscribe,
} from "@/pwa";

/** Let the app settle before asking for anything. */
const APPEAR_DELAY_MS = 2500;

export function InstallBanner() {
  const { canPrompt, standalone, installed } = useSyncExternalStore(
    subscribe,
    getInstallState,
  );
  const platform = mobilePlatform();

  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(() => installBannerSnoozed());
  // Manual "here's how" steps — the iOS path, and the Android fallback for
  // when no prompt was ever offered.
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => {
    snoozeInstallBanner();
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      setDismissed(true);
    } else if (outcome === "dismissed") {
      // Declined the OS sheet — treat it as "not now" rather than re-asking.
      dismiss();
    } else {
      // No prompt to fire after all; fall back to telling them where it lives.
      setShowSteps(true);
    }
  }, [dismiss]);

  // Desktop shell, desktop browser, already installed, snoozed, or too soon.
  if (isNativeHost() || !platform || standalone || installed) return null;
  if (dismissed || !ready) return null;

  return (
    <InstallBannerCard
      platform={platform}
      oneTap={canPrompt && !showSteps}
      showSteps={showSteps}
      onPrimary={canPrompt && !showSteps ? install : () => setShowSteps(true)}
      onDismiss={dismiss}
    />
  );
}

interface CardProps {
  platform: "android" | "ios";
  /** Render the real one-tap install CTA rather than "add to home screen". */
  oneTap: boolean;
  /** Replace the CTA with the manual gesture steps. */
  showSteps: boolean;
  onPrimary: () => void;
  onDismiss: () => void;
}

/**
 * The card itself, with every environment check already made.
 *
 * Split from the gate above so it can be rendered — and looked at — in any
 * state without spoofing a user agent or waiting out the appear delay. The
 * gate decides *whether*; this decides *how it looks*.
 */
export function InstallBannerCard({
  platform,
  oneTap,
  showSteps,
  onPrimary,
  onDismiss,
}: CardProps) {
  return (
    <div
      role="region"
      aria-label="Install OmniWerk"
      className="md:hidden fixed inset-x-3 bottom-3 z-[60] install-banner-enter"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="overflow-hidden rounded-xl border border-space-30 bg-surface shadow-modal">
        {/* Brand hairline — the one flash of accent on an otherwise quiet card. */}
        <div className="h-[3px] bg-gradient-to-r from-jove via-hydra to-oxy" />

        <div className="p-4">
          <div className="flex items-start gap-3">
            <img
              src="/icons/icon-192.png"
              alt=""
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 rounded-xl ring-1 ring-space-30"
            />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-space">Keep OmniWerk one tap away</p>
              <p className="mt-0.5 text-xs leading-relaxed text-space-50">
                Add it to your home screen for a faster start and more room to focus.
              </p>
            </div>

            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="-m-1.5 shrink-0 rounded-lg p-1.5 text-space-40 transition-colors hover:bg-space-20 hover:text-space"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {showSteps ? (
            <InstallSteps platform={platform} />
          ) : (
            <button
              type="button"
              onClick={onPrimary}
              className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5
                         text-sm font-semibold text-on-primary shadow-sm transition-all duration-150
                         hover:bg-primary-hover active:scale-[0.98]
                         focus:outline-none focus:ring-2 focus:ring-jove-60 focus:ring-offset-2"
            >
              {oneTap ? (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v12m0 0l-4-4m4 4l4-4M4 19h16"
                    />
                  </svg>
                  Install app
                </>
              ) : (
                "Add to home screen"
              )}
            </button>
          )}

          {/* Escape hatch for anyone who'd rather have the real desktop build. */}
          <a
            href={NATIVE_DOWNLOADS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-3 flex items-center justify-center gap-1.5 border-t border-space-30 pt-3
                       text-xs text-space-50"
          >
            <span>
              On a computer?{" "}
              <span className="font-medium text-jove group-hover:underline">
                Download the desktop app
              </span>
            </span>
            <svg
              className="h-3 w-3 shrink-0 text-jove"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 6H18v4.5M17.5 6.5L10 14M15 14v4a1 1 0 01-1 1H6a1 1 0 01-1-1v-8a1 1 0 011-1h4"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

/** The manual gesture, spelled out. iOS has no install API at all, and a
 *  Chromium that never fired `beforeinstallprompt` won't grow one on request —
 *  in both cases the honest answer is to point at the menu. */
function InstallSteps({ platform }: { platform: "android" | "ios" }) {
  const steps =
    platform === "ios"
      ? [
          { icon: <ShareIcon />, text: "Tap the Share button in Safari's toolbar" },
          { icon: <PlusSquareIcon />, text: "Choose “Add to Home Screen”" },
        ]
      : [
          { icon: <DotsIcon />, text: "Open the browser menu (⋮)" },
          { icon: <PlusSquareIcon />, text: "Choose “Install app” or “Add to Home screen”" },
        ];

  return (
    <ol className="mt-3.5 space-y-2 rounded-lg bg-jove-30 p-3">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-2.5 text-xs text-space">
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-surface text-jove ring-1 ring-space-30"
            aria-hidden="true"
          >
            {s.icon}
          </span>
          <span className="leading-snug">{s.text}</span>
        </li>
      ))}
    </ol>
  );
}

/* iOS share glyph — an open-topped tray with an arrow rising out of it. */
function ShareIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15V3m0 0L8 7m4-4l4 4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12H5v8h14v-8h-1" />
    </svg>
  );
}

function PlusSquareIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path strokeLinecap="round" d="M12 9v6M9 12h6" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}
